import { useCallback, useRef, useEffect, useState } from 'react';
import { Check, Play, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import PhotoSwipeLightbox from 'photoswipe/lightbox';
import 'photoswipe/style.css';
import { useTranslation } from 'react-i18next';

/**
 * Génère l'URL d'une photo avec les paramètres de format
 * @param {string} uniqId - L'identifiant unique de la photo
 * @param {'thumbnail'|'mini'|'fullscreen'|'original'} format - Le format de l'image
 * @returns {string} L'URL complète
 */
export function getPhotoUrl(uniqId, format = 'original') {
    return `/api/gateway/api/pictures/${uniqId}?format=${format}&band=false`;
}

/**
 * URL de streaming vidéo (endpoint Range côté API).
 */
export function getVideoStreamUrl(uniqId) {
    return `/api/gateway/api/pictures/${uniqId}/video`;
}

/**
 * URL d'une photo pour un invité (session par cookie, sans compte) : les routes
 * `/api/pictures/*` exigent un utilisateur authentifié, l'invité passe par les routes
 * de son invitation. À renseigner dans `photo.thumbnailUrl` / `photo.originalUrl`.
 * @param {string} invitationUuid
 * @param {string} uniqId
 * @param {'thumbnail'|'mini'|'fullscreen'|'original'} format
 */
export function getGuestPhotoUrl(invitationUuid, uniqId, format = 'original') {
    return `/api/gateway/api/public/procedures/invitations/${invitationUuid}/intervention/pictures/${uniqId}?format=${format}&band=false`;
}

/**
 * URL de streaming vidéo pour un invité.
 */
export function getGuestVideoStreamUrl(invitationUuid, uniqId) {
    return `/api/gateway/api/public/procedures/invitations/${invitationUuid}/intervention/pictures/${uniqId}/video`;
}

/**
 * Détecte si un document est une vidéo, à partir de son `mime` (source de vérité côté API)
 * ou à défaut de son `mediaType` (utilisé côté UI pour les médias fraîchement capturés).
 */
export function isVideoDoc(doc) {
    if (!doc) return false;
    if (doc.mediaType === 'video') return true;
    return typeof doc.mime === 'string' && doc.mime.startsWith('video/');
}

/**
 * Chemin React vers la page de détail d'un média : `/videos/{id}` si vidéo, `/pictures/{id}` sinon.
 * Retourne null si le doc n'a pas d'`uniq_id`.
 */
export function getMediaDetailPath(doc) {
    if (!doc?.uniq_id) return null;
    return (isVideoDoc(doc) ? '/videos/' : '/pictures/') + doc.uniq_id;
}

/**
 * Hook générique pour gérer une lightbox PhotoSwipe
 * Peut être utilisé avec n'importe quelle liste d'images
 *
 * @param {Array<{src: string, width?: number, height?: number, alt?: string}>} images - Liste des images
 * @returns {{openLightbox: function}}
 */
export function useLightbox(images = []) {
    const lightboxRef = useRef(null);

    // Cleanup lightbox on unmount
    useEffect(() => {
        return () => {
            if (lightboxRef.current) {
                lightboxRef.current.destroy();
                lightboxRef.current = null;
            }
        };
    }, []);

    const openLightbox = useCallback(
        (index = 0) => {
            const dataSource = images.map((image, i) => ({
                src: image.src,
                width: image.width || 1920,
                height: image.height || 1080,
                alt: image.alt || `Image ${i + 1}`,
            }));

            if (dataSource.length === 0) return;

            const lightbox = new PhotoSwipeLightbox({
                dataSource,
                pswpModule: () => import('photoswipe'),
                showHideAnimationType: 'zoom',
                bgOpacity: 1,
            });

            lightbox.init();
            lightbox.loadAndOpen(index);
            lightboxRef.current = lightbox;
        },
        [images],
    );

    return { openLightbox };
}

/**
 * Hook spécialisé pour les photos horodatées avec préchargement des dimensions
 *
 * @param {Array<{uniq_id: string}>} photos - Liste des photos horodatées
 * @returns {{openLightbox: function, imageDimensions: object}}
 */
export function usePhotoLightbox(photos = []) {
    const [imageDimensions, setImageDimensions] = useState({});

    // Précharger les dimensions des images originales
    useEffect(() => {
        photos.forEach((photo) => {
            if (photo.uniq_id && !imageDimensions[photo.uniq_id]) {
                const img = new Image();
                img.onload = () => {
                    setImageDimensions((prev) => ({
                        ...prev,
                        [photo.uniq_id]: { width: img.naturalWidth, height: img.naturalHeight },
                    }));
                };
                img.src = photo.originalUrl || getPhotoUrl(photo.uniq_id, 'original');
            }
        });
    }, [photos, imageDimensions]);

    // Construire la liste d'images pour useLightbox
    const images = photos
        .filter((p) => p.uniq_id)
        .map((photo) => {
            const dims = imageDimensions[photo.uniq_id] || { width: 1920, height: 1080 };
            return {
                // originalUrl : URL alternative (ex. routes invité), sinon route authentifiée
                src: photo.originalUrl || getPhotoUrl(photo.uniq_id, 'original'),
                width: dims.width,
                height: dims.height,
                alt: `Photo ${photo.uniq_id}`,
            };
        });

    const { openLightbox } = useLightbox(images);

    return { openLightbox, imageDimensions };
}

/**
 * Composant thumbnail pour afficher une photo en miniature
 *
 * @param {Object} props
 * @param {{uniq_id?: string, previewUrl?: string, thumbnailUrl?: string}} props.photo - La photo à afficher
 * @param {number} [props.index=0] - Index de la photo
 * @param {boolean} [props.showRemoveButton=false] - Afficher le bouton de suppression
 * @param {function} [props.onRemove] - Callback pour supprimer la photo
 * @param {boolean} [props.showHorodateBadge=true] - Afficher le badge "Horodatée"
 * @param {function} [props.onClick] - Callback au clic sur la photo
 * @param {number|string} [props.width] - Largeur de la thumbnail (ex: 200, "200px", "100%")
 * @param {number|string} [props.height] - Hauteur de la thumbnail (ex: 200, "200px", "100%")
 */
export function PhotoThumbnail({
    photo,
    index = 0,
    showRemoveButton = false,
    onRemove,
    showHorodateBadge = true,
    onClick,
    width,
    height,
}) {
    const { t } = useTranslation();
    const isVideo =
        photo.mediaType === 'video' || (typeof photo.mime === 'string' && photo.mime.startsWith('video/'));

    // thumbnailUrl explicite (ex. routes invité), sinon la thumbnail authentifiée si on a un uniq_id,
    // sinon la previewUrl (pour les photos en cours d'upload)
    const thumbnailUrl =
        photo.thumbnailUrl || (photo.uniq_id ? getPhotoUrl(photo.uniq_id, 'thumbnail') : photo.previewUrl);

    // Gérer la taille
    const sizeStyle = {};
    if (width) {
        sizeStyle.width = typeof width === 'number' ? `${width}px` : width;
    }
    if (height) {
        sizeStyle.height = typeof height === 'number' ? `${height}px` : height;
    }

    return (
        <div
            className={`relative aspect-square rounded-[12px] overflow-hidden group ${onClick ? 'cursor-pointer' : ''}`}
            style={sizeStyle}
            onClick={() => onClick && onClick(index)}
        >
            {thumbnailUrl ? (
                <img
                    src={thumbnailUrl}
                    alt={isVideo ? `Vidéo ${index + 1}` : `Photo ${index + 1}`}
                    className="w-full h-full object-cover transition-transform group-hover:scale-105"
                    loading="lazy"
                />
            ) : (
                <div className="w-full h-full bg-muted flex items-center justify-center">
                    <Check className="h-6 w-6 text-secondary" />
                </div>
            )}

            {/* Overlay play pour les vidéos */}
            {isVideo && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="bg-black/50 rounded-full p-[10px]">
                        <Play className="h-5 w-5 text-white fill-white" />
                    </div>
                </div>
            )}

            {/* Badge horodaté */}
            {showHorodateBadge && photo.uniq_id && (
                <div className="absolute bottom-[6px] left-[6px] bg-secondary rounded-[4px] px-[6px] py-[2px]">
                    <span className="text-[10px] text-white font-medium">{t('common:horodatee')}</span>
                </div>
            )}

            {/* Bouton de suppression */}
            {showRemoveButton && onRemove && (
                <Button
                    variant="plain"
                    color="destructive"
                    size="auto"
                    onClick={(e) => {
                        e.stopPropagation();
                        onRemove(index);
                    }}
                    className="absolute top-[6px] right-[6px] w-[24px] h-[24px] bg-destructive rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                >
                    <X className="h-3 w-3 text-white" />
                </Button>
            )}

            {/* Overlay pour indiquer qu'on peut cliquer */}
            {onClick && photo.uniq_id && (
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors pointer-events-none" />
            )}
        </div>
    );
}

export default useLightbox;
